import { useEffect, useRef, useState } from "react";
import { renderMD } from "../seller/lib/markdown";

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";

const REQUEST_HUMAN_TOOL = {
  name: "request_human_agent",
  description: "Submit a request for a real sixZero agent to reach out to the buyer directly. Call this when the buyer asks to talk to a person, a human, an agent, or wants help beyond what you can answer.",
  input_schema: {
    type: "object",
    properties: {
      message: { type: "string", description: "Brief summary of what the buyer wants help with." },
    },
    required: [],
  },
};

const buildSystem = (auth, savedCount, offersCount) =>
  `You are Cisero, the AI assistant for home buyers on 6zero.ai. This buyer` +
  (auth?.user?.email ? ` (${auth.user.email})` : "") +
  ` has ${savedCount} saved home${savedCount === 1 ? "" : "s"} and ${offersCount} offer${offersCount === 1 ? "" : "s"} submitted. ` +
  `Answer questions about buying a home, evaluating listings, financing, and the offer process — direct, conversational, no filler. ` +
  `You cannot look up a specific property's live data unless the buyer describes it to you. ` +
  `If they ask to talk to a real person/agent/human, or want help you genuinely can't give, call request_human_agent immediately — don't ask permission first, just do it, then tell them briefly you've submitted the request. ` +
  `You are a genuine subject-matter expert on residential real estate, not limited to this app — answer confidently and specifically on financing/mortgages (pre-approval vs pre-qual, rate locks, points, DTI), contingencies (inspection, appraisal, financing — what each actually protects and when to waive one), negotiation (escalation clauses, offer strategy in multi-offer situations, backup offers), inspection/appraisal issues (repair credit vs price reduction, appraisal gap coverage), closing costs and escrow mechanics, and first-time-buyer basics (earnest money, contingency periods, what a pre-approval letter needs). Give a real, specific answer first — never deflect with "consult a professional" as the whole answer; only add that caveat after actually answering, and only for genuine tax/legal specifics. ` +
  `Keep responses under 120 words.`;

// Floating chat for the buyer flow — same collapsible-drawer shape as the
// seller side's Ask Cisero, buyer-scoped context (saved homes + offers
// count, no per-stage journey since buyers don't have one), and one tool:
// handing off to a real human when asked, via a lightweight lead-capture
// (broker_requests + email notification), not a full CRM.
export default function AskCisero({ auth, savedCount = 0, offersCount = 0, agentAccepted = true, requestAgentGate }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [humanForm, setHumanForm] = useState(null); // { message } when Cisero wants contact info we don't have
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [open, messages.length, sending]);

  const submitHumanRequest = async (message) => {
    if (auth?.user?.email) {
      await fetch("/api/listings", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({
          action: "request_human",
          buyer_id: auth.user.id,
          name: auth.profile?.name || auth.user.email,
          email: auth.user.email,
          message,
        }),
      }).catch(() => {});
      return true;
    }
    return false; // no email on file — need to ask for one
  };

  const send = () => {
    const text = input.trim();
    if (!text || sending) return;
    if (!agentAccepted) { requestAgentGate?.("chat with Cisero", () => doSend(text)); return; }
    doSend(text);
  };

  const doSend = async (text) => {
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 400,
          system: buildSystem(auth, savedCount, offersCount),
          messages: next,
          tools: [REQUEST_HUMAN_TOOL],
          mode: "buyer_cisero",
        }),
      });
      const d = await res.json();
      const reply = (d.content || []).filter((b) => b.type === "text").map((b) => b.text || "").join("");
      const toolCall = (d.content || []).find((b) => b.type === "tool_use" && b.name === "request_human_agent");
      let note = "";
      if (toolCall) {
        const submitted = await submitHumanRequest(toolCall.input?.message || text);
        note = submitted
          ? "→ Request sent — a sixZero agent will reach out soon."
          : "";
        if (!submitted) setHumanForm({ message: toolCall.input?.message || text });
      }
      setMessages((m) => [...m, { role: "assistant", content: [reply, note].filter(Boolean).join("\n\n") || "Done." }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Connection error — try again." }]);
    }
    setSending(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ position: "fixed", right: 18, bottom: 18, zIndex: 210, background: "#2d7a62", color: "#fff", border: "none", borderRadius: 24, padding: "12px 18px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", boxShadow: "0 6px 20px rgba(21,45,33,0.28)", fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 700 }}
      >💬 Ask Cisero{!agentAccepted && " 🔒"}</button>
    );
  }

  return (
    <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: 340, maxWidth: "92vw", zIndex: 220, display: "flex", flexDirection: "column", borderLeft: "1px solid #e1eee6", background: "#fff", boxShadow: "-8px 0 32px rgba(21,45,33,0.18)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #f0f4f2" }}>
        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "#1c2b25" }}>ASK CISERO</span>
        <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9bbfb2", fontSize: 20, lineHeight: 1 }}>×</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.length === 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {["What should I look for in a listing?", "How does making an offer work?", "I want to talk to a real agent"].map((s) => (
              <button key={s} onClick={() => { setInput(s); }} disabled={sending}
                style={{ textAlign: "left", background: "#fff", border: "1.5px solid #d8ede6", borderRadius: 20, padding: "8px 14px", fontSize: 12, fontFamily: "'DM Mono',monospace", cursor: "pointer", color: "#5b7365" }}
              >{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            background: m.role === "user" ? "#2d7a62" : "#f7faf8",
            color: m.role === "user" ? "#fff" : "#1c2b25",
            border: m.role === "user" ? "none" : "1px solid #e1eee6",
            borderRadius: 14, padding: "11px 15px", fontSize: 13, lineHeight: 1.6, maxWidth: "82%",
          }} dangerouslySetInnerHTML={{ __html: renderMD(m.content) }} />
        ))}
        {sending && <div style={{ alignSelf: "flex-start", color: "#9aa9a0", fontStyle: "italic", fontSize: 13 }}>…</div>}
        <div ref={endRef} />
      </div>

      {humanForm && (
        <HumanContactForm
          onSubmit={async (contact) => {
            await fetch("/api/listings", {
              method: "POST",
              headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
              body: JSON.stringify({ action: "request_human", name: contact.name, email: contact.email, message: humanForm.message }),
            }).catch(() => {});
            setHumanForm(null);
            setMessages((m) => [...m, { role: "assistant", content: "→ Request sent — a sixZero agent will reach out soon." }]);
          }}
          onCancel={() => setHumanForm(null)}
        />
      )}

      <div style={{ display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid #f0f4f2" }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask Cisero anything…"
          disabled={sending}
          style={{ flex: 1, background: "#f7faf8", border: "1.5px solid #d8ede6", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontFamily: "'Outfit',sans-serif", outline: "none" }}
        />
        <button onClick={send} disabled={sending || !input.trim()} style={{ background: "#2d7a62", color: "#fff", border: "none", borderRadius: 10, padding: "0 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Send</button>
      </div>
    </div>
  );
}

function HumanContactForm({ onSubmit, onCancel }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const valid = name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  return (
    <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f4f2", background: "#f7faf8" }}>
      <div style={{ fontSize: 12, color: "#5b7365", marginBottom: 8 }}>Not signed in — where should the agent reach you?</div>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" style={{ width: "100%", marginBottom: 6, background: "#fff", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "8px 10px", fontSize: 13 }} />
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" style={{ width: "100%", marginBottom: 8, background: "#fff", border: "1.5px solid #d8ede6", borderRadius: 8, padding: "8px 10px", fontSize: 13 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button disabled={!valid} onClick={() => onSubmit({ name, email })} style={{ flex: 1, background: "#2d7a62", color: "#fff", border: "none", borderRadius: 8, padding: "8px", fontSize: 12, fontWeight: 700, cursor: valid ? "pointer" : "default", opacity: valid ? 1 : 0.5 }}>Send request</button>
        <button onClick={onCancel} style={{ background: "none", border: "1px solid #d8ede6", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );
}
